import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment,
  Badge,
  Stack,
  TablePagination
} from '@mui/material';

import {
  Search as SearchIcon,
  Person as PersonIcon,
  Science as ScienceIcon,
  Scanner as ScannerIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  LocalHospital as HospitalIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Thermostat as TempIcon,
  Favorite as PulseIcon,
  Air as BreathIcon,
  MonitorWeight as WeightIcon,
  Height as HeightIcon,
  Calculate as BMIIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ChildCare as ChildCareIcon,
  Clear as ClearIcon,
  PlaylistAddCheck as ActionsIcon
} from '@mui/icons-material';

import { loadScanPatients, loadScanRequestsOutpatient, loadScanRequestsInpatient, loadScanParameters as loadScanParametersAPI, saveScanResults, loadExistingScanResults, loadPatientScanReport, loadPatientScanResultsToday, loadPharmacyInfo, loadPatientDocumentation } from '../BaseURL';
import ScanPrintDialog from './ScanPrintDialog';
import Swal from 'sweetalert2';
import MedicalHistoryDialog from '../Doctors/MedicalHistoryDialog';
import { ProgressChip, AdvanceProgressButton } from '../Common/RequestProgress';

const RequestedScan = () => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [patientScans, setPatientScans] = useState([]);
  const [filterType, setFilterType] = useState('all'); // 'all' | 'InPatient' | 'OutPatient'
  const [filterLoading, setFilterLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Worklist (patient table) state — mirrors MyPatients.js
  const [quickSearchId, setQuickSearchId] = useState('');
  const [patientPage, setPatientPage] = useState(0);
  const [patientRowsPerPage, setPatientRowsPerPage] = useState(10);
  const [patientOrderBy, setPatientOrderBy] = useState('fullName');
  const [patientOrder, setPatientOrder] = useState('asc');

  // Results Entry Dialog States
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedTestForResults, setSelectedTestForResults] = useState(null);
  const [scanParameters, setScanParameters] = useState([]);
  // 'parameter' (default — value + flag entry) | 'narrative' (paragraph
  // finding per section + a single clinical Impression at the bottom).
  // Set by loadScanParameters from the backend; drives which UI branch
  // renders inside the entry dialog and which report template the
  // ScanPrintDialog auto-locks to.
  const [scanFormat, setScanFormat] = useState('parameter');
  const [scanImpression, setScanImpression] = useState('');
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [parameterResult, setParameterResult] = useState('');
  // Flag starts empty — the radiographer picks one only when it matters
  // (abnormal finding, urgent, etc.). Auto-defaulting to the first flag
  // option silently labelled normal scans as e.g. "high" when the user
  // never touched the dropdown.
  const [parameterFlag, setParameterFlag] = useState('');
  // Tracks whether we've already auto-picked the first unanswered
  // parameter for the current Enter Results dialog session. We auto-pick
  // exactly once per open so the user can deselect/cancel without us
  // immediately re-selecting on the next render.
  const autoPickedScanRef = useRef(false);
  // One-shot flag: true once we've seeded the entry cards with each
  // parameter's configured default finding for this dialog session.
  // Reset in handleEnterResults so the next open re-seeds.
  const defaultsSeededScanRef = useRef(false);
  const [enteredResults, setEnteredResults] = useState([]);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [loadingParameterDetails, setLoadingParameterDetails] = useState(false);
  const [scanCategory, setScanCategory] = useState('');
  const [availableFlags, setAvailableFlags] = useState(['-']);
  const [editingIndex, setEditingIndex] = useState(null);
  const [generalComment, setGeneralComment] = useState('');

  // Report Dialog States
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState({});
  const [reportLoading, setReportLoading] = useState(false);

  // Print-template picker state (Branded / Standard) for today's scan
  // results. Mirrors the lab-side flow so the user can pick a layout and
  // supply Verified/Approved signatories before the print window opens.
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printReportData, setPrintReportData] = useState({});
  // Per-scan format hint (from migration 040 via loadPatientScanReport)
  // — passes into ScanPrintDialog so narrative-marked scans render
  // through the descriptive template branch automatically.
  const [printScanFormats, setPrintScanFormats] = useState({});
  const [printPatient, setPrintPatient]       = useState(null);

  // Documentation Dialog States
  const [documentationDialogOpen, setDocumentationDialogOpen] = useState(false);
  const [documentationData, setDocumentationData] = useState(null);
  const [documentationLoading, setDocumentationLoading] = useState(false);

  // Medical History Dialog State
  const [medicalHistoryOpen, setMedicalHistoryOpen] = useState(false);

  const searchInputRef = useRef(null);

  // Get user info from localStorage
  const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};

  // Company info state
  const [companyInfo, setCompanyInfo] = useState(null);

  // Load company info
  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const response = await fetch(loadPharmacyInfo);
        const data = await response.json();
        if (data.success && data.results) {
          setCompanyInfo(data.results);
        }
      } catch (error) {
        console.error('Error loading company info:', error);
      }
    };
    loadCompanyInfo();
  }, []);

  // Load all patients once on mount — filter chips apply client-side
  useEffect(() => {
    loadPatients('all');
  }, []);

  const loadPatients = async (state = 'all') => {
    setFilterLoading(true);
    try {
      const response = await fetch(loadScanPatients(state));
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setSearchResults(data);
      } else {
        console.error('Unexpected response format:', data);
        setSearchResults([]);
      }
      setFilterLoading(false);
    } catch (error) {
      console.error('Error loading patients:', error);
      setSearchResults([]);
      setFilterLoading(false);
    }
  };

  // Load patient scan requests when patient is selected
  useEffect(() => {
    if (selectedPatient) {
      loadPatientScanRequests(selectedPatient.id, selectedPatient.state);
    }
  }, [selectedPatient]);

  // Check for stored scan data from dashboard edit on component mount
  useEffect(() => {
    const storedScanData = localStorage.getItem('scanEditData');
    if (storedScanData) {
      try {
        const scanData = JSON.parse(storedScanData);
        console.log('Loading scan data from dashboard edit:', scanData);
        
        // Set the selected patient and scan data
        setSelectedPatient({
          id: scanData.patient_id,
          name: scanData.patient_name,
          state: scanData.patient_state || 'outpatient'
        });
        
        // Load the patient's scan requests first, then open results entry for the specific scan
        loadPatientScanRequests(scanData.patient_id, scanData.patient_state || 'outpatient').then(() => {
          // Find the specific scan in the loaded requests and open results entry
          setTimeout(() => {
            const scanToEdit = {
              ...scanData,
              patient_id: scanData.patient_id
            };
            handleEnterResults(scanToEdit);
          }, 500); // Small delay to ensure scans are loaded
        });
        
        // Clear the stored data
        localStorage.removeItem('scanEditData');
      } catch (error) {
        console.error('Error parsing stored scan data:', error);
        localStorage.removeItem('scanEditData');
      }
    }
  }, []);

  const loadPatientScanRequests = async (patientId, patientState) => {
    try {
      // Load scan requests for the patient
      const endpoint = patientState === 'inpatient' 
        ? loadScanRequestsInpatient
        : loadScanRequestsOutpatient;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ patientId })
      });
      
      const data = await response.json();
      
      if (data.scanRequests && Array.isArray(data.scanRequests)) {
        // Add patient_id to each scan request
        const scansWithPatientId = data.scanRequests.map(scan => ({
          ...scan,
          patient_id: patientId
        }));
        setPatientScans(scansWithPatientId);
      } else {
        setPatientScans([]);
      }
    } catch (error) {
      console.error('Error loading patient scan requests:', error);
      setPatientScans([]);
    }
  }

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm('');
  };

  const handleBackToWorklist = () => {
    setSelectedPatient(null);
    setPatientScans([]);
    setPage(0);
  };

  // Match on the digits only so staff can type "125" and find "OPD-125", "LAB-125", etc.
  const handleQuickSearchById = () => {
    const idQuery = quickSearchId.trim();
    if (!idQuery) return;

    const digitsOf = (val) => String(val ?? '').replace(/\D/g, '');
    const queryDigits = digitsOf(idQuery);

    const match = searchResults.find((p) => {
      if (String(p.id).toLowerCase() === idQuery.toLowerCase()) return true;
      const idDigits = digitsOf(p.id);
      return queryDigits && idDigits && idDigits === queryDigits;
    });

    if (match) {
      setSelectedPatient(match);
      setQuickSearchId('');
    } else {
      Swal.fire({
        icon: 'info',
        title: 'No patient found',
        text: `No patient with ID "${idQuery}" was found on the active scan list.`,
        confirmButtonColor: '#1A9B7F',
      });
    }
  };

  const handleViewDocumentation = async () => {
    if (!selectedPatient) {
      Swal.fire({
        icon: 'warning',
        title: 'No Patient Selected',
        text: 'Please select a patient first.',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    setDocumentationLoading(true);
    try {
      const url = `${loadPatientDocumentation}?patientId=${encodeURIComponent(selectedPatient.id)}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.success && data.data) {
        setDocumentationData(data.data);
        setDocumentationDialogOpen(true);
      } else {
        Swal.fire({
          icon: 'info',
          title: 'No Documentation',
          text: 'No documentation records found for this patient.',
          confirmButtonColor: '#1976d2'
        });
      }
    } catch (error) {
      console.error('Error loading documentation:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load patient documentation.',
        confirmButtonColor: '#d32f2f'
      });
    } finally {
      setDocumentationLoading(false);
    }
  };

  const handleCloseDocumentation = () => {
    setDocumentationDialogOpen(false);
    setDocumentationData(null);
  };

  // Normalise the state field returned by the backend into a stable canonical form.
  const canonicalState = (raw) => {
    const s = String(raw || '').toLowerCase();
    if (s === 'inpatient') return 'InPatient';
    if (s === 'outpatient') return 'OutPatient';
    return raw || '—';
  };

  const initialsOf = (name = '') =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';

  // Counts for the filter chips
  const patientCounts = useMemo(() => {
    const c = { all: searchResults.length, InPatient: 0, OutPatient: 0 };
    searchResults.forEach(p => {
      const s = canonicalState(p.state);
      if (s === 'InPatient') c.InPatient++;
      else if (s === 'OutPatient') c.OutPatient++;
    });
    return c;
  }, [searchResults]);

  // Apply search + status filter, then sort
  const visiblePatients = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    const filtered = searchResults.filter(p => {
      const s = canonicalState(p.state);
      if (filterType === 'InPatient' && s !== 'InPatient') return false;
      if (filterType === 'OutPatient' && s !== 'OutPatient') return false;
      if (!q) return true;
      return (
        (p.fullName || '').toLowerCase().includes(q)
        || String(p.id || '').toLowerCase().includes(q)
        || String(p.tel || '').includes(q)
        || (p.Insurance || '').toLowerCase().includes(q)
      );
    });
    const cmp = (a, b) => {
      const av = patientOrderBy === 'state' ? canonicalState(a.state) : a[patientOrderBy];
      const bv = patientOrderBy === 'state' ? canonicalState(b.state) : b[patientOrderBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv));
    };
    filtered.sort((a, b) => patientOrder === 'asc' ? cmp(a, b) : -cmp(a, b));
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, searchTerm, filterType, patientOrderBy, patientOrder]);

  const pagedPatients = visiblePatients.slice(
    patientPage * patientRowsPerPage,
    patientPage * patientRowsPerPage + patientRowsPerPage
  );

  const handlePatientSort = (field) => {
    if (patientOrderBy === field) {
      setPatientOrder(patientOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setPatientOrderBy(field);
      setPatientOrder('asc');
    }
  };

  // Reset to first page if filters shrink the list past the current page
  useEffect(() => {
    if (patientPage > 0 && pagedPatients.length === 0 && visiblePatients.length > 0) {
      setPatientPage(0);
    }
  }, [pagedPatients.length, visiblePatients.length, patientPage]);

  // Results Entry Functions
  const handleEnterResults = async (scan) => {
    setSelectedTestForResults(scan);
    setResultsDialogOpen(true);
    setScanCategory(scan.category);
    // Re-arm the one-shot auto-pick so the upcoming dialog selects the
    // first unanswered parameter once the parameter + result loaders
    // settle. The companion useEffect performs the actual pick.
    autoPickedScanRef.current = false;
    // Re-arm the one-shot default-finding seed too — see the companion
    // useEffect below.
    defaultsSeededScanRef.current = false;
    setSelectedParameter(null);
    setParameterResult('');
    setParameterFlag('');
    // Reset narrative-mode fields so the previous scan's Impression
    // doesn't bleed into a fresh open. loadScanParameters will re-set
    // scanFormat once it resolves; the impression fills only if
    // loadExistingScanResults returns one.
    setScanFormat('parameter');
    setScanImpression('');

    // Load scan parameters based on scan name
    await loadScanParameters(scan.scan_name);

    // Load existing results for today
    await loadTodayExistingResults(scan);
  };

  // ─── Auto-pick the first unanswered parameter on dialog open ──────────
  // Runs once each time the Enter Results dialog opens, after both the
  // parameter list and the existing-results list have loaded. Mirrors how
  // the lab Result Entry dialog jumps straight into the first parameter
  // so the radiographer can start typing immediately.
  useEffect(() => {
    if (!resultsDialogOpen) return;
    if (autoPickedScanRef.current) return;
    if (!scanParameters || scanParameters.length === 0) return;

    const answeredIds = new Set(enteredResults.map((r) => r.parameterId));
    const next = scanParameters.find((p) => !answeredIds.has(p.id));
    if (next) {
      autoPickedScanRef.current = true;
      handleParameterSelect(next);
    } else if (scanParameters.length > 0) {
      // Every parameter already has a result — still mark the dialog as
      // "auto-picked" so we don't keep re-entering this branch.
      autoPickedScanRef.current = true;
    }
    // handleParameterSelect is a stable function on this component so
    // omitting it from deps is safe; we intentionally only react to the
    // loaded data changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsDialogOpen, scanParameters, enteredResults]);

  // ── Seed default findings on dialog open ─────────────────────────────
  // Once per dialog session, walk the parameter list and pre-fill any
  // parameter whose `defaultFinding` is non-empty AND doesn't already
  // have an entered result (from a prior save or this session). The
  // radiographer sees the template narrative pre-populated and only has
  // to edit rather than type from scratch — the whole point of the
  // Setup Scan "Default Finding" field. A clear-to-empty is preserved
  // (the row is dropped from enteredResults on empty setValue) because
  // the seed only runs once per open.
  useEffect(() => {
    if (!resultsDialogOpen) return;
    if (defaultsSeededScanRef.current) return;
    if (!scanParameters || scanParameters.length === 0) return;

    const paramsWithDefault = scanParameters.filter(
      (p) => (p.defaultFinding || '').trim() !== ''
    );
    if (paramsWithDefault.length === 0) {
      defaultsSeededScanRef.current = true;
      return;
    }

    setEnteredResults((prev) => {
      const answered = new Set(prev.map((r) => r.parameterId));
      const answeredByName = new Set(
        prev.map((r) => String(r.parameterName || '').toLowerCase())
      );
      const additions = paramsWithDefault
        .filter(
          (p) => !answered.has(p.id) &&
                 !answeredByName.has(String(p.name || '').toLowerCase())
        )
        .map((p) => ({
          parameterId:   p.id,
          parameterName: p.name,
          result:        p.defaultFinding,
          flag:          'normal',
          valueType:     p.valueType || 'text',
          unit:          p.unit || '',
          refRange:      p.refRange || '',
        }));
      if (additions.length === 0) return prev;
      return [...prev, ...additions];
    });
    defaultsSeededScanRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsDialogOpen, scanParameters, enteredResults]);

  const loadTodayExistingResults = async (scan) => {
    try {
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

      const response = await fetch(loadExistingScanResults, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: scan.patient_id,
          scanName: scan.scan_name,
          date: currentDate
        })
      });

      const data = await response.json();

      if (data.success && data.results && data.results.length > 0) {
        // Transform existing results to match our format
        const transformedResults = data.results.map(result => ({
          parameterId: result.id,
          parameterName: result.para,
          result: result.finding,
          flag: result.flag || '-'
        }));
        setEnteredResults(transformedResults);
        // Narrative scans also carry a shared Impression — surface it in
        // the sidebar text area so the tech can edit it alongside the
        // sections instead of having to retype the conclusion.
        setScanImpression(data.impression || '');

        // Results already exist for this scan today — explicitly ask the
        // radiographer whether they want to update. If they say "Yes,
        // update", we leave the dialog open with the existing rows
        // loaded so they can edit. If they cancel, we close the dialog
        // so nothing gets accidentally overwritten.
        const confirmUpdate = await Swal.fire({
          title: 'Results already entered',
          html: `Results have been entered for <b>${scan.scan_name}</b> on this patient today (${transformedResults.length} parameter${transformedResults.length === 1 ? '' : 's'} saved).<br/><br/>Do you want to update them?`,
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Yes, update',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#11998e',
          cancelButtonColor: '#9ca3af',
          reverseButtons: true,
        });
        if (!confirmUpdate.isConfirmed) {
          // Bail out cleanly — close the dialog and reset state.
          setResultsDialogOpen(false);
          setEnteredResults([]);
          setSelectedParameter(null);
          setSelectedTestForResults(null);
        }
      } else {
        setEnteredResults([]);
      }
    } catch (error) {
      setEnteredResults([]);
    }
  };

  const loadScanParameters = async (scanName) => {
    setLoadingParameters(true);
    try {
      const response = await fetch(loadScanParametersAPI(scanName));
      const data = await response.json();

      if (data.success && data.parameters) {
        // Transform API response to match expected format. Carry through
        // the per-parameter metadata added in migration 021 — the entry
        // dialog uses these to pre-fill the result box and to switch on
        // the range parser + scale chart for numeric parameters.
        const transformedParameters = data.parameters.map((param, index) => ({
          id: param.id,
          name: param.paramater,
          flags: param.Flag && param.Flag.trim() !== '' ? param.Flag.split(',').map(flag => flag.trim()) : [],
          displayOrder:    param.display_order   ?? index,
          defaultFinding:  param.default_finding ?? '',
          valueType:       param.value_type      || 'text',
          unit:            param.unit            ?? '',
          refRange:        param.ref_range       ?? '',
        }));
        setScanParameters(transformedParameters);
        // scanFormat comes from the top-level of the response (migration
        // 040). Falls back to 'parameter' for legacy scans that predate
        // the column so existing installs keep the standard entry UI.
        setScanFormat(data.scanFormat === 'narrative' ? 'narrative' : 'parameter');
      } else {
        setScanParameters([]);
        setScanFormat('parameter');
      }
    } catch (error) {
      console.error('Error loading scan parameters:', error);
      setScanParameters([]);
    } finally {
      setLoadingParameters(false);
    }
  };

  const handleParameterSelect = async (parameter) => {
    setSelectedParameter(parameter);
    // Pre-fill the result box with the configured default finding (e.g.
    // "Normal in size and echotexture.") — the radiographer can accept
    // it, tweak it, or clear it. Numeric parameters typically leave the
    // default blank so the radiologist types the measurement directly.
    setParameterResult(parameter.defaultFinding || '');
    // Flag stays empty until the user explicitly picks one. Auto-picking
    // the first flag (e.g. "high") was tagging routine results with the
    // wrong flag whenever the dropdown was left untouched.
    setParameterFlag('');
    // Set available flags for this parameter
    setAvailableFlags(parameter.flags && parameter.flags.length > 0 ? parameter.flags : ['-']);
  };

  const handleAddResult = () => {
    if (!selectedParameter || !parameterResult.trim()) return;

    const newResult = {
      parameterId: selectedParameter.id,
      parameterName: selectedParameter.name,
      result: parameterResult,
      flag: parameterFlag,
      // Carry the parameter metadata onto the saved row so the printed
      // report can render the numeric range-scale bar and the unit chip.
      valueType: selectedParameter.valueType || 'text',
      unit:      selectedParameter.unit || '',
      refRange:  selectedParameter.refRange || '',
    };

    let updatedResults;
    if (editingIndex !== null) {
      // Update existing result
      updatedResults = [...enteredResults];
      updatedResults[editingIndex] = newResult;
      setEnteredResults(updatedResults);
      setEditingIndex(null);
    } else {
      // Reject duplicate parameter entries — every parameter should appear
      // at most once per (patient, scan, date). If the user accidentally
      // tried to add the same one twice, point them at the existing row
      // and let them edit it from there instead of stacking duplicates.
      const dupIdx = enteredResults.findIndex((r) =>
        (r.parameterId && selectedParameter?.id && r.parameterId === selectedParameter.id) ||
        (r.parameterName && selectedParameter?.name &&
          String(r.parameterName).toLowerCase() === String(selectedParameter.name).toLowerCase())
      );
      if (dupIdx !== -1) {
        Swal.fire({
          title: 'Parameter already added',
          html: `<b>${selectedParameter.name}</b> already has a result in this scan.<br/><br/>Edit the existing row below to change it, or clear the duplicate first.`,
          icon: 'warning',
          confirmButtonColor: '#11998e',
          confirmButtonText: 'OK',
        });
        return;
      }
      // Add new result
      updatedResults = [...enteredResults, newResult];
      setEnteredResults(updatedResults);
    }

    // ── Auto-advance to the next unanswered parameter ──────────────
    // Walk the parameter list (already in display_order from the API)
    // and select the first one whose id isn't in the updated result
    // set. When everything has a result, just clear the form so the
    // radiographer can scroll up and review before saving.
    const answeredIds = new Set(updatedResults.map((r) => r.parameterId));
    const nextParam = scanParameters.find((p) => !answeredIds.has(p.id));
    if (nextParam) {
      handleParameterSelect(nextParam);
    } else {
      setSelectedParameter(null);
      setParameterResult('');
      setParameterFlag('');
    }
  };

  const handleSaveResults = async () => {
    if (enteredResults.length === 0) return;

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Save Results?',
      text: 'Do you want to save these scan results?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    // Show progress
    Swal.fire({
      title: 'Saving...',
      text: 'Please wait while we save your results',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      // Get current user and date
      const currentUser = userInfo.user_name || userInfo.name || 'Unknown';
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

      // Prepare data for API. Narrative scans also carry a clinical
      // Impression (one per session, not per parameter) — sent alongside
      // the results and applied uniformly to every scan_result row on
      // the backend.
      const saveData = {
        patientId: selectedTestForResults.patient_id,
        scanName: selectedTestForResults.scan_name,
        results: enteredResults.map(result => ({
          parameterName: result.parameterName,
          result: result.result,
          flag: result.flag
        })),
        user: currentUser,
        date: currentDate,
        impression: scanFormat === 'narrative' ? scanImpression.trim() : '',
      };

      // Call the API to save results
      const response = await fetch(saveScanResults, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saveData)
      });

      const data = await response.json();

      if (data.success) {
        // Close progress dialog and show success
        Swal.fire({
          title: 'Done!',
          text: 'Scan results have been saved successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        // Keep the scan status as is (don't set to completed)
        // This allows editing again without navigating away
        const updatedScans = patientScans.map(scan =>
          scan.id === selectedTestForResults.id
            ? { ...scan, results: enteredResults }
            : scan
        );
        setPatientScans(updatedScans);

        // Reset form
        setEnteredResults([]);
        setGeneralComment('');
        setResultsDialogOpen(false);
        setSelectedTestForResults(null);
        setScanParameters([]);
      } else {
        Swal.fire({
          title: 'Error',
          text: 'Failed to save results. Please try again.',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving results:', error);
      Swal.fire({
        title: 'Error',
        text: 'An error occurred while saving. Please try again.',
        icon: 'error'
      });
    }
  };

  const handleViewReport = async () => {
    if (!selectedPatient) return;
    
    setReportLoading(true);
    try {
      const response = await fetch(loadPatientScanReport, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: selectedPatient.id
        })
      });

      const data = await response.json();

      if (data.success) {
        setReportData(data.reportData || {});
        setReportDialogOpen(true);
      }
      setReportLoading(false);
    } catch (error) {
      setReportLoading(false);
    }
  };

  const handleCloseReport = () => {
    setReportDialogOpen(false);
    setReportData({});
  };

  const handleViewTodayResults = async () => {
    try {
      const response = await fetch(loadPatientScanResultsToday, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: selectedPatient?.id
        })
      });

      const data = await response.json();

      if (data.success && data.reportData && Object.keys(data.reportData).length > 0) {
        // Hand off to the shared print-template picker so the user can pick
        // between the original layout and the new branded layout (and supply
        // Verified/Approved signatories) before the print window opens.
        setPrintReportData(data.reportData);
        setPrintScanFormats(data.scanFormats || {});
        setPrintPatient(selectedPatient);
        setPrintDialogOpen(true);
      } else {
        Swal.fire({
          icon: 'info',
          title: 'No Results',
          text: 'No scan results found for today.'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load today\'s results.'
      });
    }
  };

  const handleEditResult = (index) => {
    const result = enteredResults[index];
    const parameter = scanParameters.find(p => p.id === result.parameterId);
    setSelectedParameter(parameter || {
      id: result.parameterId,
      name: result.parameterName
    });
    setParameterResult(result.result);
    setParameterFlag(result.flag);
    // Set available flags for this parameter
    setAvailableFlags(parameter && parameter.flags && parameter.flags.length > 0 ? parameter.flags : ['-']);
    setEditingIndex(index);
  };

  const handleRemoveResult = (index) => {
    const updatedResults = enteredResults.filter((_, i) => i !== index);
    setEnteredResults(updatedResults);
    if (editingIndex === index) {
      setEditingIndex(null);
      setSelectedParameter(null);
      setParameterResult('');
      setParameterFlag('');
    }
  };

  // Reorder helpers — swap with the neighbour above/below.
  // Order is preserved end-to-end: the backend save uses array order to
  // assign IDs, and the report query reads back in id order.
  const handleMoveResult = (index, direction) => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= enteredResults.length) return;
    const next = [...enteredResults];
    [next[index], next[target]] = [next[target], next[index]];
    setEnteredResults(next);
    // Keep editing pointer aligned with the row that moved.
    if (editingIndex === index) setEditingIndex(target);
    else if (editingIndex === target) setEditingIndex(index);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      default: return 'default';
    }
  };



  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Page header — toggles between worklist title and patient breadcrumb+back */}
      {selectedPatient ? (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            px: { xs: 2, md: 2.5 },
            py: 1.5,
            border: '1px solid', borderColor: 'divider',
            borderRadius: 2.5,
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)',
          }}
        >
          <Button
            onClick={handleBackToWorklist}
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#1A9B7F',
              borderRadius: 1.75,
              px: 1.75,
              '&:hover': { bgcolor: 'rgba(26,155,127,0.08)' },
            }}
          >
            Back to Worklist
          </Button>
          <Box sx={{ width: '1px', height: 28, bgcolor: '#e5e9ef' }} />
          <Box sx={{ minWidth: 0, flexShrink: 1 }}>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 700,
              color: '#1A9B7F', letterSpacing: '1.4px',
              textTransform: 'uppercase', lineHeight: 1,
            }}>
              Patient Scans
            </Typography>
            <Typography sx={{
              fontSize: { xs: '1rem', md: '1.15rem' },
              fontWeight: 700,
              color: 'text.primary',
              mt: 0.25,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {selectedPatient.fullName}
              <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary', ml: 1, fontSize: '0.88rem' }}>
                · ID {selectedPatient.id}
              </Box>
            </Typography>
          </Box>

          {/* Quick-jump search by patient ID */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <TextField
              size="small"
              value={quickSearchId}
              onChange={(e) => setQuickSearchId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleQuickSearchById();
                }
              }}
              placeholder="Patient ID…"
              sx={{
                width: { xs: 140, md: 180 },
                '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: 'background.paper' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearchId && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQuickSearchId('')} sx={{ color: 'text.secondary' }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              onClick={handleQuickSearchById}
              disabled={!quickSearchId.trim()}
              variant="contained"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 1.75,
                bgcolor: '#1A9B7F',
                px: 2,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#15826A', boxShadow: 'none' },
              }}
            >
              Search
            </Button>
          </Box>
        </Paper>
      ) : (
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          Requested Scans
        </Typography>
      )}

      {/* Patient Worklist — only when no patient is selected */}
      {!selectedPatient && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2, md: 2.5 },
            border: '1px solid', borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: '0 2px 12px rgba(15, 23, 42, 0.04)',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontSize: '0.72rem', fontWeight: 700,
              color: '#1A9B7F', letterSpacing: '1.4px',
              textTransform: 'uppercase', mb: 0.5,
            }}>
              Radiology Workspace
            </Typography>
            <Typography sx={{
              fontSize: { xs: '1.25rem', md: '1.45rem' },
              fontWeight: 700, color: 'text.primary',
              letterSpacing: '-0.3px', lineHeight: 1.2,
              mb: 0.5,
            }}>
              Patient Worklist
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem' }}>
              {(() => {
                const n = searchResults.length;
                if (filterLoading && n === 0) return 'Loading patients…';
                if (n === 0) return 'No patients currently have requested scans.';
                const word = n === 1 ? 'patient' : 'patients';
                return `${n} ${word} with requested scans. Click any row to open the chart.`;
              })()}
            </Typography>
          </Box>

          {/* Filter chips + search */}
          <Paper elevation={0} sx={{
            p: 1.5, mb: 2,
            border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper',
          }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                {[
                  { key: 'all',        label: 'All',        count: patientCounts.all,        color: 'text.secondary' },
                  { key: 'InPatient',  label: 'Inpatient',  count: patientCounts.InPatient,  color: '#15803D' },
                  { key: 'OutPatient', label: 'Outpatient', count: patientCounts.OutPatient, color: '#A16207' },
                ].map(f => {
                  const active = filterType === f.key;
                  return (
                    <Chip
                      key={f.key}
                      label={`${f.label} (${f.count})`}
                      onClick={() => { setFilterType(f.key); setPatientPage(0); }}
                      size="small"
                      sx={{
                        height: 30,
                        fontWeight: 600, fontSize: '0.78rem',
                        bgcolor: active ? f.color : '#fff',
                        color:   active ? '#fff' : '#334155',
                        border: '1px solid',
                        borderColor: active ? f.color : '#e5e9ef',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: active ? f.color : `${f.color}0D`, borderColor: f.color },
                      }}
                    />
                  );
                })}
              </Stack>

              <TextField
                size="small"
                inputRef={searchInputRef}
                placeholder="Search by name, ID, phone or insurance…"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setPatientPage(0); }}
                sx={{
                  width: { xs: '100%', md: 340 },
                  '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: 'background.paper' },
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ color: 'text.secondary' }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Paper>

          {/* Patient table */}
          <Paper elevation={0} sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper',
          }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' } }}>
                    {[
                      { id: 'fullName',  label: 'Patient',      sortable: true,  width: '28%' },
                      { id: 'age',       label: 'Age / Gender', sortable: true,  width: '14%' },
                      { id: 'state',     label: 'Status',       sortable: true,  width: '12%' },
                      { id: 'Insurance', label: 'Insurance',    sortable: true,  width: '18%' },
                      { id: 'visitDate', label: 'Visit Date',   sortable: true,  width: '14%' },
                      { id: 'tel',       label: 'Phone',        sortable: false, width: '12%' },
                      { id: 'actions',   label: '',             sortable: false, width: '2%', align: 'right' },
                    ].map(c => (
                      <TableCell
                        key={c.id}
                        align={c.align || 'left'}
                        sx={{
                          width: c.width,
                          fontWeight: 700, fontSize: '0.72rem',
                          color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px',
                          py: 1.25,
                        }}
                      >
                        {c.sortable ? (
                          <TableSortLabel
                            active={patientOrderBy === c.id}
                            direction={patientOrderBy === c.id ? patientOrder : 'asc'}
                            onClick={() => handlePatientSort(c.id)}
                            sx={{ '& .MuiTableSortLabel-icon': { fontSize: 16 } }}
                          >
                            {c.label}
                          </TableSortLabel>
                        ) : c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filterLoading && searchResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                        <CircularProgress size={28} sx={{ color: '#1A9B7F' }} />
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mt: 1.5 }}>
                          Loading patients…
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : pagedPatients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                        <PersonIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                          {searchTerm || filterType !== 'all'
                            ? 'No patients match your filters.'
                            : 'No patients to show.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : pagedPatients.map((p) => {
                    const state = canonicalState(p.state);
                    const isInpatient = state === 'InPatient';
                    //  Under 12; at 12 the patient is an adult. Same boundary as
                    //  utils/age.js and the prescribing lists.
                    const isChild = (p.age != null) && Number(p.age) < 12;
                    return (
                      <TableRow
                        key={p.id}
                        hover
                        onClick={() => handlePatientSelect(p)}
                        sx={{
                          cursor: 'pointer',
                          '& td': { borderBottom: '1px solid #f1f5f9', py: 1.25 },
                          '&:hover .row-arrow': { opacity: 1, transform: 'translateX(2px)' },
                          '&:last-child td': { borderBottom: 'none' },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{
                              width: 36, height: 36, fontSize: 13, fontWeight: 700,
                              bgcolor: isChild ? '#FCE7F3' : 'rgba(26,155,127,0.12)',
                              color:   isChild ? '#BE185D' : '#1A9B7F',
                            }}>
                              {isChild ? <ChildCareIcon sx={{ fontSize: 18 }} /> : initialsOf(p.fullName)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{
                                fontWeight: 600, fontSize: '0.88rem', color: 'text.primary',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {p.fullName || 'Unknown'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                ID: {p.id}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.primary' }}>
                            {p.age ?? '—'} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>yrs</Box>
                          </Typography>
                          <Chip
                            label={p.gender || '—'}
                            size="small"
                            sx={{
                              mt: 0.25, height: 18, fontSize: '0.65rem', fontWeight: 700,
                              bgcolor: String(p.gender).toUpperCase() === 'MALE' ? '#EFF6FF' : '#FDF2F8',
                              color:   String(p.gender).toUpperCase() === 'MALE' ? '#1D4ED8' : '#BE185D',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={state}
                            size="small"
                            sx={{
                              height: 22, fontSize: '0.7rem', fontWeight: 700,
                              bgcolor: isInpatient ? '#DCFCE7' : '#FEF3C7',
                              color:   isInpatient ? '#15803D' : '#A16207',
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                            {p.Insurance || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                            {p.visitDate
                              ? new Date(p.visitDate).toISOString().slice(0, 10)
                              : '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            {p.tel || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title="Open chart" arrow>
                            <IconButton
                              size="small"
                              className="row-arrow"
                              onClick={(e) => { e.stopPropagation(); handlePatientSelect(p); }}
                              sx={{
                                color: '#1A9B7F',
                                opacity: 0.55,
                                transition: 'opacity .15s ease, transform .15s ease',
                                '&:hover': { bgcolor: 'rgba(26,155,127,0.12)', opacity: 1 },
                              }}
                            >
                              <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {visiblePatients.length > patientRowsPerPage && (
              <TablePagination
                component="div"
                count={visiblePatients.length}
                page={patientPage}
                onPageChange={(_, p) => setPatientPage(p)}
                rowsPerPage={patientRowsPerPage}
                onRowsPerPageChange={(e) => { setPatientRowsPerPage(parseInt(e.target.value, 10)); setPatientPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: '1px solid #e5e9ef' }}
              />
            )}
          </Paper>
        </Paper>
      )}

      {/* Patient detail panel — bio/vitals + scans, only when a patient is selected */}
      {selectedPatient && (
        <>
          <Card
            sx={{
              mb: 3,
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '100%',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
                zIndex: 1
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
              <Grid container spacing={2} wrap="nowrap">
                {/* Patient Profile Section */}
                <Grid item xs={2.4}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #0fd34aff 0%, #319249ff 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '-50%',
                        right: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                        pointerEvents: 'none'
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 100,
                        height: 100,
                        bgcolor: 'rgba(255, 255, 255, 0.25)',
                        border: '4px solid rgba(255, 255, 255, 0.3)',
                        mb: 2,
                        fontSize: '2.5rem',
                        fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 1
                      }}
                    >
                      {selectedPatient.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'P'}
                    </Avatar>
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.1)', zIndex: 1 }}>
                      {selectedPatient.fullName}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, opacity: 0.95, zIndex: 1 }}>
                      {selectedPatient.age} years old
                    </Typography>
                    <Box
                      sx={{
                        px: 2.5,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        mb: 2,
                        zIndex: 1
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        ID: {selectedPatient.id}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, zIndex: 1, flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
                      <Chip
                        label={selectedPatient.gender || '—'}
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          color: 'white',
                          fontWeight: 600,
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}
                      />
                      <Chip
                        label={selectedPatient.Insurance || 'N/A'}
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          color: 'white',
                          fontWeight: 600,
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}
                      />
                    </Stack>
                    <Chip
                      label={selectedPatient.state || '—'}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.25)',
                        color: 'white',
                        fontWeight: 600,
                        px: 2,
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 1
                      }}
                    />
                  </Box>
                </Grid>

                {/* Contact Information Section */}
                <Grid item xs={2.4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: '#f8f9fd',
                      border: '1px solid #e8eaf6',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          mr: 2
                        }}
                      >
                        <PhoneIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} color="primary">
                        Contact Info
                      </Typography>
                    </Box>

                    <Stack spacing={2.5}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Phone Number
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                          {selectedPatient.tel || 'Not provided'}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Location
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                          {selectedPatient.res || 'Not provided'}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Visit Date
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, color: 'primary.main' }}>
                          {selectedPatient.visitDate || '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Vital Signs Section */}
                <Grid item xs={2.4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: '#fff3e0',
                      border: '1px solid #ffe0b2',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(255, 152, 0, 0.15)',
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                          mr: 2
                        }}
                      >
                        <HospitalIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#e65100' }}>
                        Vital Signs
                      </Typography>
                    </Box>

                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Temperature
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.temperature || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#e65100' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Blood Pressure
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.bp || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#e65100' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Pulse
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.pulse || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#e65100' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Respiratory Rate
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.respiration || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#e65100' }}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Measurements Section */}
                <Grid item xs={2.4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: '#e8f5e9',
                      border: '1px solid #c8e6c9',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(76, 175, 80, 0.15)',
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                          mr: 2
                        }}
                      >
                        <ScienceIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#2e7d32' }}>
                        Measurements
                      </Typography>
                    </Box>

                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Weight
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.weight || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(76, 175, 80, 0.15)', color: '#2e7d32' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Height
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.height || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(76, 175, 80, 0.15)', color: '#2e7d32' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          BMI
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.bmi || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(76, 175, 80, 0.15)', color: '#2e7d32' }}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Actions Section */}
                <Grid item xs={2.4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: '#f3e8ff',
                      border: '1px solid #ddd6fe',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(124, 58, 237, 0.18)',
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                          mr: 2
                        }}
                      >
                        <ActionsIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#5b21b6' }}>
                        Actions
                      </Typography>
                    </Box>

                    <Stack spacing={1.25}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={handleViewTodayResults}
                        disabled={patientScans.length === 0}
                        sx={{
                          textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                          py: 0.9,
                          backgroundColor: '#43a047',
                          boxShadow: 'none',
                          '&:hover': { backgroundColor: '#388e3c', boxShadow: '0 4px 12px rgba(67,160,71,0.25)' }
                        }}
                      >
                        Today's Results
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={handleViewReport}
                        disabled={reportLoading || patientScans.length === 0}
                        sx={{
                          textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                          py: 0.9,
                          backgroundColor: '#1976d2',
                          boxShadow: 'none',
                          '&:hover': { backgroundColor: '#1565c0', boxShadow: '0 4px 12px rgba(25,118,210,0.25)' }
                        }}
                      >
                        {reportLoading ? <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} /> : null}
                        View Report
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={handleViewDocumentation}
                        disabled={documentationLoading}
                        sx={{
                          textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                          py: 0.9,
                          borderColor: '#ff9800', color: '#ff9800',
                          '&:hover': {
                            borderColor: '#f57c00',
                            backgroundColor: 'rgba(255, 152, 0, 0.06)'
                          }
                        }}
                      >
                        {documentationLoading ? <CircularProgress size={16} sx={{ mr: 1, color: '#ff9800' }} /> : null}
                        View Documentation
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={() => setMedicalHistoryOpen(true)}
                        sx={{
                          textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                          py: 0.9,
                          borderColor: '#7c3aed', color: '#7c3aed',
                          '&:hover': {
                            borderColor: '#5b21b6',
                            backgroundColor: 'rgba(124, 58, 237, 0.06)'
                          }
                        }}
                      >
                        Medical History
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Patient Scans */}
          <Card>
          <CardHeader
            title={`Scans for ${selectedPatient.fullName}`}
            avatar={<ScannerIcon />}
            action={
              <Chip
                label={`${patientScans.length} scan${patientScans.length === 1 ? '' : 's'}`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            }
          />
          <CardContent sx={{ p: 0 }}>
            {patientScans.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <ScannerIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No scans requested for this patient
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Scan Name</TableCell>
                      <TableCell>Requested By</TableCell>
                      <TableCell>Requested Date</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patientScans
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((scan) => (
                      <TableRow key={scan.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                              <ScannerIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {scan.scan_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {scan.scan_code}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>{scan.doc_name}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {scan.requested_date}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {scan.requested_time}
                          </Typography>
                        </TableCell>
                        {/* Progress tracker — chip shows the current stage
                            (paid / sample_taken / processing / done); the
                            button advances to the next stage. Backend
                            auto-flips to "done" when results are saved. */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <ProgressChip status={scan.progress_status} dense />
                            <AdvanceProgressButton
                              reqNo={scan.req_no}
                              status={scan.progress_status}
                              onAdvanced={(toStatus) => {
                                setPatientScans(prev => prev.map(item =>
                                  item === scan ? { ...item, progress_status: toStatus } : item
                                ));
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {scan.status !== 'completed' && (
                              // Same call-to-action language as the
                              // lab-side "Today's Lab Requests" table and
                              // the Scan Dashboard — teal→green gradient
                              // button with white "Enter Results" text.
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleEnterResults(scan)}
                                sx={{
                                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  px: 2.5,
                                  boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
                                    boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
                                  }
                                }}
                              >
                                Enter Results
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={patientScans.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            )}
          </CardContent>
        </Card>
        </>
      )}

      {/* Results Entry Dialog */}
      <Dialog
        open={resultsDialogOpen}
        onClose={() => setResultsDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', height: '92vh' } }}
      >
        {/* Gradient header strip — scan name + patient context + close */}
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #6a11cb 100%)',
              px: 3, py: 2.25,
              color: 'white',
              display: 'flex', alignItems: 'center', gap: 2,
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.28)',
              backdropFilter: 'blur(10px)',
            }}>
              <ScannerIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.4px',
                textTransform: 'uppercase', opacity: 0.85, lineHeight: 1,
              }}>
                Enter Scan Results
              </Typography>
              <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mt: 0.4, lineHeight: 1.2 }} noWrap>
                {selectedTestForResults?.scan_name || '—'}
              </Typography>
              {selectedPatient && (
                <Typography sx={{ fontSize: '0.82rem', opacity: 0.9, mt: 0.25 }} noWrap>
                  {selectedPatient.fullName} · ID {selectedPatient.id}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={() => setResultsDialogOpen(false)}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.12)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
              }}
            >
              <CancelIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: 'action.hover', p: { xs: 2, md: 3 } }}>
          {/* All-at-once entry — every parameter is a card with its own
              text area. Removes the old "click parameter → type result →
              click Add" three-step flow: the radiologist can walk down
              the list top-to-bottom and type as they go. Results sync
              into enteredResults on every keystroke via the helper below
              so the existing save handler + report generator work
              unchanged. */}
          {scanParameters.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
                No parameters configured for this scan. Add them in Setup Scan first.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {scanParameters.map((param, idx) => {
                // Look up any existing entry for this parameter — populated
                // either by a prior save (edit flow) or by the user typing
                // in this session. Undefined until the user first types.
                const entry = enteredResults.find(
                  (r) => (r.parameterId != null && r.parameterId === param.id) ||
                         String(r.parameterName || '').toLowerCase() === String(param.name || '').toLowerCase()
                );
                const currentValue = entry?.result ?? '';
                const currentFlag  = entry?.flag ?? '';
                const isNumeric = param.valueType === 'numeric';

                // Upsert / remove into enteredResults on every keystroke.
                // Empty value → drop the entry so the save-side "at least
                // one result" guard still works correctly.
                const setValue = (v) => {
                  if (!v.trim()) {
                    setEnteredResults((prev) => prev.filter(
                      (r) => r.parameterId !== param.id &&
                             String(r.parameterName || '').toLowerCase() !== String(param.name || '').toLowerCase()
                    ));
                    return;
                  }
                  setEnteredResults((prev) => {
                    const existingIdx = prev.findIndex(
                      (r) => (r.parameterId != null && r.parameterId === param.id) ||
                             String(r.parameterName || '').toLowerCase() === String(param.name || '').toLowerCase()
                    );
                    const row = {
                      parameterId:   param.id,
                      parameterName: param.name,
                      result:        v,
                      flag:          existingIdx >= 0 ? (prev[existingIdx].flag || 'normal') : 'normal',
                      valueType:     param.valueType || 'text',
                      unit:          param.unit || '',
                      refRange:      param.refRange || '',
                    };
                    if (existingIdx >= 0) {
                      const next = [...prev];
                      next[existingIdx] = { ...next[existingIdx], ...row };
                      return next;
                    }
                    return [...prev, row];
                  });
                };
                const setFlag = (f) => {
                  setEnteredResults((prev) => {
                    const existingIdx = prev.findIndex(
                      (r) => (r.parameterId != null && r.parameterId === param.id) ||
                             String(r.parameterName || '').toLowerCase() === String(param.name || '').toLowerCase()
                    );
                    if (existingIdx < 0) return prev;   // no value yet, nothing to flag
                    const next = [...prev];
                    next[existingIdx] = { ...next[existingIdx], flag: f };
                    return next;
                  });
                };
                const filled = currentValue.trim().length > 0;
                const stripeColor = filled ? '#15803d' : '#94a3b8';

                return (
                  <Paper
                    key={param.id}
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: filled ? 'rgba(21,128,61,0.35)' : 'divider',
                      bgcolor: 'background.paper',
                      overflow: 'hidden',
                      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                      '&:hover': {
                        borderColor: filled ? 'rgba(21,128,61,0.55)' : 'rgba(96,165,250,0.35)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
                      },
                    }}
                  >
                    {/* Card header — parameter number, name, and any
                        numeric metadata (unit + reference range) so the
                        radiologist sees the bounds without hunting. */}
                    <Box sx={{
                      px: 2, py: 1.25,
                      borderBottom: '1px solid', borderColor: 'divider',
                      display: 'flex', alignItems: 'center', gap: 1.25,
                      bgcolor: filled ? 'rgba(21,128,61,0.06)' : 'action.hover',
                    }}>
                      <Box sx={{
                        width: 6, height: 26, borderRadius: 0.5, bgcolor: stripeColor, flexShrink: 0,
                      }} />
                      <Typography sx={{
                        fontSize: '0.72rem', fontWeight: 800, color: 'text.disabled',
                        letterSpacing: '0.6px', flexShrink: 0,
                      }}>
                        {String(idx + 1).padStart(2, '0')}
                      </Typography>
                      <Typography sx={{
                        fontSize: '1rem', fontWeight: 700, color: 'text.primary',
                        flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {param.name}
                      </Typography>
                      {isNumeric && (
                        <Chip
                          size="small"
                          label={`Numeric${param.unit ? ` · ${param.unit}` : ''}`}
                          sx={{
                            height: 22, fontWeight: 700, fontSize: '0.7rem',
                            bgcolor: 'rgba(96,165,250,0.18)', color: '#60a5fa',
                            border: '1px solid rgba(96,165,250,0.30)',
                          }}
                        />
                      )}
                      {param.refRange && (
                        <Chip
                          size="small"
                          label={`Ref: ${param.refRange}`}
                          sx={{
                            height: 22, fontWeight: 700, fontSize: '0.7rem',
                            bgcolor: 'action.hover', color: 'text.secondary',
                            border: '1px dashed', borderColor: 'divider',
                          }}
                        />
                      )}
                      {filled && (
                        <Chip
                          size="small"
                          label="Filled"
                          sx={{
                            height: 22, fontWeight: 700, fontSize: '0.68rem',
                            bgcolor: 'rgba(21,128,61,0.18)', color: '#4ade80',
                            border: '1px solid rgba(74,222,128,0.30)',
                          }}
                        />
                      )}
                    </Box>

                    {/* Body — one bigger text area per parameter + a small
                        flag dropdown on the side. Numeric parameters get a
                        single-line input; everything else is multiline so
                        the radiologist has real room to describe findings. */}
                    <Box sx={{
                      p: 2,
                      display: 'grid',
                      gap: 1.25,
                      gridTemplateColumns: { xs: '1fr', md: '1fr 200px' },
                      alignItems: 'flex-start',
                    }}>
                      <TextField
                        fullWidth
                        multiline={!isNumeric}
                        minRows={isNumeric ? 1 : 4}
                        maxRows={isNumeric ? 1 : 12}
                        placeholder={isNumeric
                          ? `Enter ${param.unit || 'value'}`
                          : `Enter finding for ${param.name}…`}
                        value={currentValue}
                        onChange={(e) => setValue(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'action.hover',
                            fontSize: '0.92rem',
                            lineHeight: 1.5,
                          },
                        }}
                      />
                      <FormControl size="small" fullWidth>
                        <InputLabel>Flag</InputLabel>
                        <Select
                          value={currentFlag}
                          onChange={(e) => setFlag(e.target.value)}
                          label="Flag"
                          displayEmpty
                          disabled={!filled}
                        >
                          <MenuItem value=""><em>— None —</em></MenuItem>
                          {availableFlags.map((flag) => (
                            <MenuItem key={flag} value={flag.toLowerCase()}>{flag}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}

          {/* Compact "filled" summary — replaces the old Entered Results
              tracker card. Every parameter card above shows its own
              result inline now, so a full second list of the same rows
              was clutter. This one line just gives the tech a
              progress-at-a-glance count before they hit Save. */}
          <Box sx={{
            mb: 2, px: 2, py: 1.25, borderRadius: 1.5,
            bgcolor: 'action.hover',
            display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
          }}>
            <Box sx={{ width: 6, height: 22, borderRadius: 0.5, bgcolor: '#15803d' }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'text.primary' }}>
              {enteredResults.length} of {scanParameters.length} parameter{scanParameters.length === 1 ? '' : 's'} filled
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Chip
              size="small"
              label={enteredResults.length === scanParameters.length && scanParameters.length > 0 ? 'All done' : 'In progress'}
              sx={{
                height: 22, fontWeight: 700, fontSize: '0.7rem',
                bgcolor: (enteredResults.length === scanParameters.length && scanParameters.length > 0) ? 'rgba(21,128,61,0.18)' : 'rgba(148,163,184,0.18)',
                color:   (enteredResults.length === scanParameters.length && scanParameters.length > 0) ? '#4ade80' : 'text.secondary',
                border:  (enteredResults.length === scanParameters.length && scanParameters.length > 0) ? '1px solid rgba(74,222,128,0.30)' : '1px solid rgba(148,163,184,0.30)',
              }}
            />
          </Box>

          {/* Legacy Entered Results tracker — retained but rendered
              only if the array grew via the (unused) old add path. In
              the new every-parameter-visible flow this stays empty. */}
          {false && (
          <Paper elevation={0} sx={{
            borderRadius: 2, border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper', overflow: 'hidden', mb: 2,
          }}>
            <Box sx={{
              px: 2, py: 1.25,
              borderBottom: '1px solid', borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <Box sx={{
                width: 6, height: 22, borderRadius: 0.5,
                bgcolor: '#15803d',
              }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: 'text.primary' }}>
                Entered Results
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 1, display: { xs: 'none', sm: 'block' } }}>
                · Use ▲▼ to reorder — the report prints in this exact order.
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip
                size="small"
                label={`${enteredResults.length} saved`}
                sx={{
                  height: 20, fontWeight: 700, fontSize: '0.7rem',
                  bgcolor: 'rgba(21,128,61,0.18)', color: '#4ade80',
                  border: '1px solid rgba(74,222,128,0.30)',
                }}
              />
            </Box>
            <Box sx={{ p: enteredResults.length ? 1.25 : 0 }}>
              {enteredResults.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                    No results added yet. Pick a parameter above and click <strong>Add Result</strong>.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {enteredResults.map((result, index) => {
                    const flag = (result.flag || '').toLowerCase();
                    const isNormal = flag === 'normal' || flag === '-' || flag === '' ;
                    const flagStyle = isNormal
                      ? { bg: 'rgba(21,128,61,0.18)', text: '#4ade80', border: 'rgba(74,222,128,0.30)' }
                      : { bg: 'rgba(217,119,6,0.18)', text: '#fbbf24', border: 'rgba(251,191,36,0.32)' };
                    return (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.75,
                          border: '1px solid', borderColor: 'divider',
                          bgcolor: 'action.hover',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          transition: 'border-color .15s ease, background-color .15s ease',
                          '&:hover': { borderColor: 'rgba(96,165,250,0.4)' },
                        }}
                      >
                        <Avatar sx={{
                          width: 30, height: 30, fontSize: '0.78rem', fontWeight: 700,
                          bgcolor: 'rgba(25,118,210,0.18)', color: '#60a5fa',
                          border: '1px solid rgba(96,165,250,0.30)',
                          mt: 0.25,
                        }}>
                          {index + 1}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: 'text.primary' }}>
                              {result.parameterName}
                            </Typography>
                            <Chip
                              label={result.flag || '—'}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.68rem', fontWeight: 700,
                                bgcolor: flagStyle.bg, color: flagStyle.text,
                                border: `1px solid ${flagStyle.border}`,
                                textTransform: 'uppercase', letterSpacing: '0.4px',
                                '& .MuiChip-label': { px: 0.85 },
                              }}
                            />
                          </Stack>
                          <Typography sx={{
                            fontSize: '0.85rem', color: 'text.secondary',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          }}>
                            {result.result}
                            {result.unit ? <Box component="span" sx={{ ml: 0.5, color: 'text.disabled' }}>{result.unit}</Box> : null}
                          </Typography>
                          {result.valueType === 'numeric' && result.refRange && (
                            <Typography sx={{
                              fontSize: '0.72rem', color: 'text.secondary',
                              mt: 0.5,
                            }}>
                              Ref:&nbsp;<Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {result.refRange}
                              </Box>
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {/* Reorder buttons — disabled at boundaries */}
                          <Stack spacing={0.25}>
                            <Tooltip title="Move up" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveResult(index, 'up')}
                                  disabled={index === 0}
                                  sx={{
                                    p: 0.25,
                                    color: '#a5b4fc',
                                    bgcolor: 'rgba(84,104,255,0.10)',
                                    '&:hover': { bgcolor: 'rgba(84,104,255,0.22)' },
                                    '&.Mui-disabled': { color: 'text.disabled', bgcolor: 'transparent' },
                                  }}
                                >
                                  <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Move down" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveResult(index, 'down')}
                                  disabled={index === enteredResults.length - 1}
                                  sx={{
                                    p: 0.25,
                                    color: '#a5b4fc',
                                    bgcolor: 'rgba(84,104,255,0.10)',
                                    '&:hover': { bgcolor: 'rgba(84,104,255,0.22)' },
                                    '&.Mui-disabled': { color: 'text.disabled', bgcolor: 'transparent' },
                                  }}
                                >
                                  <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleEditResult(index)}
                              sx={{
                                color: '#60a5fa',
                                bgcolor: 'rgba(25,118,210,0.12)',
                                '&:hover': { bgcolor: 'rgba(25,118,210,0.22)' },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveResult(index)}
                              sx={{
                                color: '#f87171',
                                bgcolor: 'rgba(220,38,38,0.12)',
                                '&:hover': { bgcolor: 'rgba(220,38,38,0.22)' },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Paper>
          )}

          {/* Impression — narrative scans only. One clinical
              conclusion per session, written to every scan_result row
              on save and read back as the first non-empty value on load.
              Renders as a bordered card so the tech can't miss it. */}
          {scanFormat === 'narrative' && (
            <Paper elevation={0} sx={{
              borderRadius: 2, border: '1.5px solid',
              borderColor: 'rgba(180,83,9,0.35)',
              bgcolor: 'rgba(180,83,9,0.04)',
              p: 2, mb: 2,
            }}>
              <Typography sx={{
                fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.6px',
                textTransform: 'uppercase', color: '#b45309', mb: 1.25,
              }}>
                Clinical Impression
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                placeholder="Findings are consistent with… (or the equivalent clinical conclusion)"
                value={scanImpression}
                onChange={(e) => setScanImpression(e.target.value)}
              />
            </Paper>
          )}

          {/* General Comment */}
          <Paper elevation={0} sx={{
            borderRadius: 2, border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper', p: 2,
          }}>
            <Typography sx={{
              fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.6px',
              textTransform: 'uppercase', color: 'text.secondary', mb: 1.25,
            }}>
              General Comment
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              value={generalComment}
              onChange={(e) => setGeneralComment(e.target.value)}
              placeholder="Add any general comments or impressions..."
            />
          </Paper>
        </DialogContent>

        <DialogActions sx={{
          px: 3, py: 2,
          borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
          gap: 1,
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              {enteredResults.length === 0
                ? 'Fill at least one parameter above to enable saving.'
                : `Ready to save ${enteredResults.length} of ${scanParameters.length} parameter${scanParameters.length === 1 ? '' : 's'}.`}
            </Typography>
          </Box>
          <Button
            onClick={() => setResultsDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveResults}
            variant="contained"
            disabled={enteredResults.length === 0}
            startIcon={<SaveIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 2.5,
              background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
              boxShadow: '0 4px 12px rgba(21,128,61,0.32)',
              '&:hover': {
                background: 'linear-gradient(135deg, #166534 0%, #15803d 100%)',
                boxShadow: '0 6px 16px rgba(21,128,61,0.4)',
              },
              '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)' },
            }}
          >
            Save Results
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={handleCloseReport}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Scan Report - {selectedPatient?.fullName}
            </Typography>
            {reportLoading && <CircularProgress size={24} />}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {Object.keys(reportData).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No scan results found for this patient
              </Typography>
            </Box>
          ) : (
            <Box>
              {Object.keys(reportData)
                .sort((a, b) => new Date(b) - new Date(a)) // Sort dates descending (most recent first)
                .map((date) => (
                  <Box key={date} sx={{ mb: 4 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#1976d2',
                        borderBottom: '2px solid #1976d2',
                        pb: 1,
                        mb: 2
                      }}
                    >
                      {(() => {
                        const [year, month, day] = date.split('-').map(Number);
                        const localDate = new Date(year, month - 1, day);
                        return localDate.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        });
                      })()}
                    </Typography>

                    {Object.keys(reportData[date])
                      .sort() // Sort scan names alphabetically
                      .map((scanName) => (
                        <Box key={scanName} sx={{ ml: 2, mb: 3 }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 600,
                              color: '#424242',
                              mb: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            🔬 {scanName}
                          </Typography>

                          <TableContainer sx={{ ml: 2 }}>
                            <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Finding/Result</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Flag</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Performed By</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {reportData[date][scanName].map((result, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{result.para}</TableCell>
                                    <TableCell>{result.finding}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={result.flag}
                                        size="small"
                                        color={result.flag === 'normal' || result.flag === '-' ? 'success' : 'warning'}
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>{result.user}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ))}
                  </Box>
                ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReport} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Documentation Dialog */}
      <Dialog
        open={documentationDialogOpen}
        onClose={handleCloseDocumentation}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 3, py: 2.25,
              color: 'white',
              display: 'flex', alignItems: 'center', gap: 2,
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.28)',
              backdropFilter: 'blur(10px)',
              fontWeight: 800, fontSize: '1.1rem',
            }}>
              {(selectedPatient?.fullName || '?')
                .split(/\s+/).filter(Boolean).slice(0, 2)
                .map(n => n[0]).join('').toUpperCase()}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.4px',
                textTransform: 'uppercase', opacity: 0.85, lineHeight: 1,
              }}>
                Clinical Notes
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, mt: 0.4, lineHeight: 1.2 }}>
                Patient Documentation
              </Typography>
              {selectedPatient && (
                <Typography sx={{ fontSize: '0.82rem', opacity: 0.9, mt: 0.25 }}>
                  {selectedPatient.fullName} · ID {selectedPatient.id}
                </Typography>
              )}
            </Box>
            {documentationLoading && <CircularProgress size={24} sx={{ color: 'white' }} />}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: 'action.hover', p: { xs: 2, md: 3 } }}>
          {documentationData ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {/*
                Section accent palette — each strip carries a colored side
                stripe, a translucent tint, and a bright label color that
                stays readable against the dark theme background.
              */}
              {[
                { key: 'complaint', label: 'Chief Complaint',      text: documentationData.complaint, color: '#dc2626', labelColor: '#f87171', tint: 'rgba(220,38,38,0.10)', badge: '1' },
                { key: 'history',   label: 'History of Complaint', text: documentationData.history,   color: '#2563eb', labelColor: '#60a5fa', tint: 'rgba(37,99,235,0.12)',  badge: '2' },
                { key: 'physical',  label: 'Physical Examination', text: documentationData.physical,  color: '#15803d', labelColor: '#4ade80', tint: 'rgba(21,128,61,0.12)',  badge: '3' },
              ].map((s) => (
                <Paper
                  key={s.key}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid', borderColor: 'divider',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      borderColor: s.labelColor,
                      boxShadow: `0 4px 16px ${s.color}1a`,
                    },
                  }}
                >
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.25,
                    borderLeft: '4px solid', borderLeftColor: s.color,
                    bgcolor: s.tint,
                    borderBottom: '1px solid', borderBottomColor: 'divider',
                  }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1.5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: s.color, color: 'white',
                      fontWeight: 800, fontSize: '0.85rem',
                      boxShadow: `0 2px 6px ${s.color}66`,
                    }}>
                      {s.badge}
                    </Box>
                    <Typography sx={{
                      fontWeight: 700, color: s.labelColor,
                      fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px',
                    }}>
                      {s.label}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 3, py: 2.25 }}>
                    {s.text && String(s.text).trim() ? (
                      <Typography sx={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.75,
                        color: 'text.primary',
                        fontSize: '0.93rem',
                      }}>
                        {s.text}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.88rem' }}>
                        No {s.label.toLowerCase()} recorded for this patient.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: '50%',
                bgcolor: 'action.hover', mx: 'auto', mb: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'text.secondary', fontWeight: 700, fontSize: '1.5rem',
                border: '1px solid', borderColor: 'divider',
              }}>
                ?
              </Box>
              <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>
                No documentation available
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.5 }}>
                Nothing has been recorded for this patient yet.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: 'background.paper', px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={handleCloseDocumentation}
            variant="contained"
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 1.75,
              px: 3, boxShadow: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { boxShadow: '0 4px 12px rgba(102,126,234,0.4)' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <MedicalHistoryDialog
        open={medicalHistoryOpen}
        onClose={() => setMedicalHistoryOpen(false)}
        patientData={selectedPatient}
      />

      <ScanPrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        reportData={printReportData}
        selectedPatient={printPatient}
        userInfo={userInfo}
        companyInfo={companyInfo}
        scanFormats={printScanFormats}
      />
    </Box>
  );
};

export default RequestedScan;